import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Plus, Tag, Tags, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { BottomSheet } from "@/components/shared/PagePrimitives";

// 365: štítky dokladů — firemní seznam (název + barva, GET /doc-tags). Kategorie říká, CO se koupilo,
// štítek NA CO (Střecha, Elektro, Vícepráce). Dává se v tabulce dokladů (klik na štítek v řádku, hromadně
// u vybraných) i ve formuláři dokladu; přejmenování a mazání v okně Štítky firmy.
export const TAGS_CHANGED = "pp-doc-tags-changed";
const NEUTRAL = "#8a8f98";
const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
const plurDoc = (n) => `${n} ${n === 1 ? "doklad" : n >= 2 && n <= 4 ? "doklady" : "dokladů"}`;

export function useDocTags(enabled = true) {
  const { data = [], refetch } = useQuery({ queryKey: ["doc-tags"], queryFn: () => api.get("/doc-tags").then((r) => r.data || []), staleTime: 60_000, enabled, retry: false });
  const colorOf = (n) => data.find((t) => t.name === n)?.color || NEUTRAL;
  return { tags: data, colorOf, refetch };
}

export function TagPill({ name, color = NEUTRAL, md = false, className = "" }) {
  if (!name) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 max-w-full rounded-full font-semibold whitespace-nowrap ${md ? "h-8 px-3 text-[13px]" : "h-6 px-2.5 text-[12px]"} ${className}`}
      style={{ background: `${color}24`, color }} data-testid="tag-pill">
      <i className="w-[7px] h-[7px] rounded-[2px] shrink-0" style={{ background: color }} /><span className="truncate">{name}</span>
    </span>
  );
}

// Nabídka štítků: hledání (Enter na novém názvu štítek založí), seznam, Bez štítku, Upravit štítky…
export function TagMenu({ value, onPick, onManage, testid = "tag-menu", big = false }) {
  const { tags } = useDocTags();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [act, setAct] = useState(0);
  const ref = useRef(null);
  useEffect(() => { if (!big) ref.current?.focus({ preventScroll: true }); }, [big]);   // telefon: bez klávesnice přes seznam
  const name = q.trim();
  const shown = tags.filter((t) => !name || norm(t.name).includes(norm(name)));
  const exact = tags.some((t) => t.name.toLowerCase() === name.toLowerCase());
  const items = [...shown.map((t) => ({ k: "tag", t })), ...(name && !exact ? [{ k: "new" }] : []), ...(!name && value ? [{ k: "none" }] : [])];
  const pick = async (it) => {
    if (!it) return;
    if (it.k === "tag") return onPick(it.t.name);
    if (it.k === "none") return onPick(null);
    try { await api.post("/doc-tags", { name }); qc.invalidateQueries({ queryKey: ["doc-tags"] }); onPick(name); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); e.stopPropagation(); setAct((a) => Math.min(items.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); e.stopPropagation(); setAct((a) => Math.max(0, a - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); pick(items[act]); }
  };
  return (
    <div onKeyDown={onKey} data-testid={testid}>
      <div className={`${big ? "h-12" : "h-10"} mb-1.5 rounded-[11px] bg-secondary flex items-center gap-2 px-3`}>
        <Search size={15} className="text-muted-foreground shrink-0" aria-hidden="true" />
        <input ref={ref} value={q} onChange={(e) => { setQ(e.target.value); setAct(0); }} placeholder="Hledat nebo nový štítek…" aria-label="Štítek"
          className="bg-transparent outline-none w-full min-w-0 text-base md:text-sm" data-testid={`${testid}-search`} />
      </div>
      <div role="listbox" className={big ? "" : "max-h-[280px] overflow-y-auto"}>
        {items.map((it, i) => (
          <button key={it.k === "tag" ? it.t.name : it.k} type="button" role="option" aria-selected={it.k === "tag" && it.t.name === value} onMouseEnter={() => setAct(i)} onClick={() => pick(it)}
            data-testid={it.k === "tag" ? `${testid}-opt-${it.t.name}` : `${testid}-${it.k}`}
            className={`w-full flex items-center gap-2 ${big ? "min-h-[50px] text-base" : "min-h-[38px] text-sm"} px-2.5 rounded-[9px] text-left ${i === act ? "bg-secondary" : ""}`}>
            {it.k === "tag" ? <TagPill name={it.t.name} color={it.t.color} /> : it.k === "new"
              ? <span className="inline-flex items-center gap-1.5 font-semibold"><Plus size={15} /> Nový štítek „{name}“</span>
              : <span className="text-muted-foreground font-semibold">Bez štítku</span>}
            {it.k === "tag" && (it.t.name === value ? <Check size={16} className="ml-auto text-brand shrink-0" aria-hidden="true" />
              : it.t.count != null && <span className="ml-auto text-[12px] text-muted-foreground tabular-nums whitespace-nowrap">{plurDoc(it.t.count)}</span>)}
          </button>
        ))}
        {!items.length && <p className="px-3 py-3 text-sm text-muted-foreground">Zatím žádné štítky — napiš název a Enter.</p>}
      </div>
      {onManage && (
        <button type="button" onClick={onManage} data-testid={`${testid}-manage`}
          className={`w-full flex items-center gap-2 ${big ? "min-h-[50px] text-base" : "min-h-[38px] text-sm"} px-2.5 mt-1 pt-1 border-t border-border rounded-b-[9px] font-semibold text-muted-foreground hover:text-foreground`}>
          <Tags size={15} /> Upravit štítky…
        </button>
      )}
    </div>
  );
}

// Štítek v řádku tabulky — klik otevře nabídku přímo na místě.
export function TagCell({ name, color, onPick, onManage, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} data-testid={testid} title={name ? "Změnit štítek" : "Přidat štítek"}
          className="max-w-full min-w-0 inline-flex rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40">
          {name ? <TagPill name={name} color={color} />
            : <span className="inline-flex items-center h-6 px-2.5 rounded-full border border-dashed border-border text-[12px] font-semibold text-muted-foreground hover:text-foreground">+ štítek</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-1.5 w-[260px] rounded-[14px]" onClick={(e) => e.stopPropagation()} onOpenAutoFocus={(e) => e.preventDefault()}>
        {open && <TagMenu value={name} onPick={(v) => { setOpen(false); onPick(v); }} onManage={onManage && (() => { setOpen(false); onManage(); })} />}
      </PopoverContent>
    </Popover>
  );
}

// 368: štítek v hlavičce dokladu — barevný nadpisek nad dodavatelem (● KUCHYN ▾), bez štítku „+ 🏷 Štítek“.
// Klik (na PC i klávesa L) otevře stejný seznam jako v tabulce: na PC rozbalený pod nadpiskem, na telefonu
// spodní okno s velkými řádky. Kdo štítky měnit nesmí, vidí jen nadpisek.
export function DocTagEyebrow({ value, onChange, onManage, mobile = false, readOnly = false, hotkey = false, testid = "doc-tag" }) {
  const { colorOf } = useDocTags(!!value || !readOnly);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!hotkey || readOnly || mobile) return undefined;
    const onKey = (e) => {
      if (e.key !== "l" && e.key !== "L") return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented) return;
      const t = e.target;
      if (t?.closest?.("input, textarea, select, [contenteditable='true'], [role='listbox'], [role='menu']")) return;
      e.preventDefault(); setOpen(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [hotkey, readOnly, mobile]);
  const color = value ? colorOf(value) : null;
  const label = value ? (
    <>
      <i className="w-[9px] h-[9px] rounded-[3px] shrink-0" style={{ background: color }} aria-hidden="true" />
      <span className="truncate">{value}</span>
      {!readOnly && <ChevronDown size={14} className="shrink-0 opacity-70" aria-hidden="true" />}
    </>
  ) : (<><Plus size={14} aria-hidden="true" /><Tag size={14} aria-hidden="true" /><span>Štítek</span></>);
  const cls = "inline-flex max-w-full items-center gap-1.5 -ml-2 px-2 py-1 rounded-lg text-[12px] font-bold uppercase tracking-[0.12em] leading-none";
  if (readOnly) return value ? <span className={cls} style={{ color }} data-testid={testid}>{label}</span> : null;
  const trigger = (
    <button type="button" onClick={mobile ? () => setOpen(true) : undefined} data-testid={testid} aria-expanded={open}
      title={value ? `Změnit štítek${mobile ? "" : " (L)"}` : `Přidat štítek${mobile ? "" : " (L)"}`}
      className={`${cls} hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${value ? "" : "text-muted-foreground hover:text-foreground"}`}
      style={value ? { color } : undefined}>{label}</button>
  );
  const pick = (v) => { setOpen(false); onChange(v); };
  const manage = onManage && (() => { setOpen(false); onManage(); });
  if (mobile) {
    return (
      <>
        {trigger}
        <BottomSheet open={open} onOpenChange={setOpen} title="Štítek dokladu" testid={`${testid}-sheet`}>
          {open && <TagMenu value={value} onPick={pick} onManage={manage} big testid={`${testid}-menu`} />}
        </BottomSheet>
      </>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      {/* 369: bez portálu — seznam je uvnitř okna dokladu, jinak by nešel posouvat kolečkem myši */}
      <PopoverContent portal={false} align="start" className="p-1.5 w-[290px] rounded-[16px]" onOpenAutoFocus={(e) => e.preventDefault()}>
        {open && <TagMenu value={value} onPick={pick} onManage={manage} testid={`${testid}-menu`} />}
      </PopoverContent>
    </Popover>
  );
}

// Okno Štítky firmy: přejmenovat (projeví se na všech dokladech), smazat (z dokladů zmizí), přidat.
export function TagsManagerDialog({ open, onOpenChange, onChanged }) {
  const { tags, refetch } = useDocTags(open);
  const qc = useQueryClient();
  const [del, setDel] = useState(null);
  const [add, setAdd] = useState("");
  const done = (list) => { qc.setQueryData(["doc-tags"], list); window.dispatchEvent(new Event(TAGS_CHANGED)); onChanged?.(); };
  const rename = async (t, v) => {
    const n = v.trim();
    if (!n || n === t.name) return;
    try { const { data } = await api.patch("/doc-tags", { name: t.name, new_name: n }); done(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); refetch(); }
  };
  const remove = async (t) => {
    try {
      const { data } = await api.delete("/doc-tags", { params: { name: t.name } });
      setDel(null); done(data.tags);
      toast.success(`Štítek ${t.name} smazán${data.removed ? ` · odebrán z ${data.removed === 1 ? "1 dokladu" : `${data.removed} dokladů`}` : ""}.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const create = async () => {
    const n = add.trim(); if (!n) return;
    try { const { data } = await api.post("/doc-tags", { name: n }); setAdd(""); done(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setDel(null); onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="tags-manager">
        <DialogTitle className="text-[20px] font-extrabold">Štítky firmy</DialogTitle>
        <DialogDescription className="text-[13px]">Platí pro celou firmu. Přejmenování se projeví na všech dokladech, smazaný štítek z dokladů zmizí (doklady zůstanou).</DialogDescription>
        <div className="space-y-2 max-h-[55vh] overflow-y-auto -mx-1 px-1">
          {tags.map((t) => (del === t.name ? (
            <div key={t.name} className="flex flex-wrap items-center gap-2 rounded-[12px] bg-destructive/10 px-3 py-2.5" data-testid={`tags-manager-confirm-${t.name}`}>
              <span className="flex-1 min-w-0 text-sm">Smazat štítek <b>{t.name}</b>?{t.count ? ` Odebere se z ${t.count === 1 ? "1 dokladu" : `${t.count} dokladů`}.` : " Žádný doklad ho nemá."}</span>
              <Button variant="outline" onClick={() => setDel(null)} className="h-9 rounded-full">Zpět</Button>
              <Button variant="destructive" onClick={() => remove(t)} className="h-9 rounded-full font-bold" data-testid="tags-manager-delete-ok">Smazat</Button>
            </div>
          ) : (
            <div key={t.name} className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-[4px] shrink-0" style={{ background: t.color }} aria-hidden="true" />
              <input key={t.name} defaultValue={t.name} aria-label={`Název štítku ${t.name}`} data-testid={`tags-manager-name-${t.name}`}
                onBlur={(e) => rename(t, e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                className="flex-1 min-w-0 h-10 rounded-[11px] border border-border bg-card px-3 text-base md:text-sm outline-none focus:border-brand" />
              <span className="w-[74px] text-right text-[12.5px] text-muted-foreground tabular-nums shrink-0">{plurDoc(t.count || 0)}</span>
              <button type="button" onClick={() => setDel(t.name)} aria-label={`Smazat štítek ${t.name}`} title="Smazat" data-testid={`tags-manager-delete-${t.name}`}
                className="w-9 h-9 rounded-full grid place-items-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0"><Trash2 size={16} /></button>
            </div>
          )))}
          {!tags.length && <p className="text-sm text-muted-foreground">Zatím žádné štítky.</p>}
        </div>
        <div className="flex items-center gap-2">
          <input value={add} onChange={(e) => setAdd(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); create(); } }}
            placeholder="Nový štítek, třeba Kuchyň" aria-label="Nový štítek" data-testid="tags-manager-new"
            className="flex-1 min-w-0 h-10 rounded-[11px] border border-border bg-card px-3 text-base md:text-sm outline-none focus:border-brand" />
          <Button variant="outline" onClick={create} className="h-10 rounded-full font-semibold" data-testid="tags-manager-add"><Plus size={15} /> Přidat</Button>
        </div>
        <div className="flex md:justify-end"><Button onClick={() => onOpenChange(false)} className="w-full md:w-auto h-11 rounded-full px-6 font-bold">Hotovo</Button></div>
      </DialogContent>
    </Dialog>
  );
}
